import { useEffect, useRef, useState } from 'react';
import { TRANSFER_CERTIFICATE } from 'constants/identityMethod';
import { CREDIT_CARD, GBI, GBI_REJECTED, ROBO, ROBO_REJECTED } from 'constants/userProfile';
import { VIDEO_CALL, VIDEO_SELFIE } from 'constants/identityMethod';
import { CLIENT_RISK } from 'constants/clientRisk';
import {
    DOCUMENTATION,
    PENDING_OTP,
    STAND_BY,
    TRANSFER_OTP,
    COMPLETED,
    PRE_DOCUMENTATION,
    SUCCESS,
} from 'constants/statusProcess';

export const useDocumentationStatus = ({
    actions,
    change,
    doTagGetInformationDocumentError,
    doTagDocumentationPage,
    doTagRecordStandByPage,
    documentationActions,
    preLoaderActions,
    userProfile,
    isDocumentRetryBeenShown,
    setPageName,
}) => {
    const [isDocumentationUploaded, setIsDocumentationUploaded] = useState(false);
    const [isDocumentationUploadedCompleted, setIsDocumentationUploadedCompleted] = useState(false);
    const [isDocumentationLoaded, setIsDocumentationLoaded] = useState(false);
    const [isIdentificationDoneVirtually, setIsIdentificationDoneVirtually] = useState(null);

    const {
        accessToken,
        clientRisk,
        identityMethod,
        investmentType,
        documentationStatusCounter,
        documentationUploaded,
        documentationUploadCompleted,
        status,
    } = userProfile;

    const prevStatus = useRef(status);

    const documentationCompletedCounter = documents =>
        documents.reduce(
            (count, { status, source }) => (status === COMPLETED && source !== 'subidamanual' ? count + 1 : count),
            0
        );

    const changeUserStatusIfDocumentationIsCompleted = documents => {
        const documentationCompleted = documentationCompletedCounter(documents);

        if (investmentType === CREDIT_CARD && !documents.length) change('userProfile.status', SUCCESS);
        if (documentationCompleted === documents.length) change('userProfile.documentationUploadCompleted', true);
    };

    const getInformationDocument = () => {
        preLoaderActions.showPreLoader();

        documentationActions.getInformationDocument(accessToken, (response, data) => {
            if (!response) {
                doTagGetInformationDocumentError({ body: data.body, statusCode: data.statusCode });
                setIsDocumentationLoaded(true);
                preLoaderActions.hidePreLoader();

                return;
            }

            changeUserStatusIfDocumentationIsCompleted(data.documents);

            setIsDocumentationLoaded(true);
            preLoaderActions.hidePreLoader();
        });
    };

    useEffect(() => {
        const isDocumentsUploaded = documentationUploaded || status === STAND_BY || documentationUploadCompleted;

        setIsDocumentationUploaded(isDocumentsUploaded);
        setIsDocumentationUploadedCompleted(
            isDocumentsUploaded || documentationUploadCompleted || status === TRANSFER_OTP || status === PENDING_OTP
        );
    }, [documentationUploaded, status, documentationUploadCompleted]);

    useEffect(() => {
        if (prevStatus.current === PRE_DOCUMENTATION && status === DOCUMENTATION) getInformationDocument();

        prevStatus.current = status;
    }, [status]);

    const sendPreDocumentationOk = () => {
        preLoaderActions.showPreLoader();

        actions.preDocumentationOk(accessToken, verifiedOk => {
            if (verifiedOk) {
                change('userProfile.ibanDocumentationAcepted', true);
                getInformationDocument();
            }
        });

        preLoaderActions.hidePreLoader();
    };

    useEffect(() => {
        const finalizeDocumentationLoading = () => {
            setIsDocumentationLoaded(true);
            change('userProfile.documentationUploadCompleted', true);
            preLoaderActions.hidePreLoader2();
        };
        const handlePreDocumentation = () => {
            const isRobo = investmentType === ROBO || investmentType === ROBO_REJECTED;
            const isGBI = investmentType === GBI || investmentType === GBI_REJECTED;

            if (identityMethod === TRANSFER_CERTIFICATE && (isRobo || isGBI)) {
                sendPreDocumentationOk();
            } else {
                getInformationDocument();
            }
        };

        if (accessToken && status === DOCUMENTATION) return getInformationDocument();
        if (status !== PRE_DOCUMENTATION) return finalizeDocumentationLoading();
        if (status === PRE_DOCUMENTATION) return handlePreDocumentation();
    }, []);

    useEffect(() => {
        const isVideoIdentified = identityMethod === VIDEO_CALL || identityMethod === VIDEO_SELFIE;

        setIsIdentificationDoneVirtually(
            isVideoIdentified && clientRisk === CLIENT_RISK.LOW && investmentType !== CREDIT_CARD
        );
    }, [clientRisk, identityMethod, investmentType]);

    useEffect(() => {
        const tagPage = () => {
            if (isIdentificationDoneVirtually === null) return;
            const isPreDocumentation = status === PRE_DOCUMENTATION;
            if (
                !isIdentificationDoneVirtually &&
                !isDocumentationUploadedCompleted &&
                !isDocumentationUploaded &&
                (isDocumentationLoaded || isPreDocumentation)
            ) {
                return doTagDocumentationPage(isDocumentRetryBeenShown);
            }

            if (isDocumentationLoaded && isDocumentationUploadedCompleted) {
                return doTagRecordStandByPage();
            }
        };
        setPageName(tagPage());
    }, [
        isDocumentRetryBeenShown,
        isIdentificationDoneVirtually,
        isDocumentationLoaded,
        documentationUploadCompleted,
        isDocumentationUploadedCompleted,
        documentationStatusCounter,
        doTagDocumentationPage,
        doTagRecordStandByPage,
    ]);

    return { isDocumentationUploaded, isDocumentationLoaded };
};
